import {Box, Stack, Tooltip, Typography} from '@mui/material';
import {get_price_str} from 'common';
import {useEffect, useRef, useState} from 'react';
import HorizontalBarGraph from './HorizontalBarGraph';

export default function DoubleBarPriceGraph(props: {
	width?: string;
	current_price: number;
	order_price: number;
	target_price: number;
	loss_cut_price: number;
	heat: number;
	tag?: string;
	disabled?: boolean;
	bgcolor?: string;
}) {
	const {current_price, order_price, target_price, loss_cut_price, tag, width, disabled, heat, bgcolor} = props;
	const [is_blink_on, set_is_blink_on] = useState(false);

	const side = current_price > order_price ? 'right' : 'left';
	const disabled_color = 'grey.400';
	const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		const update_blink = () => set_is_blink_on(prev => !prev);

		if (heat > 0) {
			if (intervalIdRef.current) clearInterval(intervalIdRef.current);

			const blink_min = 50;
			const blink_max = 600;
			const blinkInterval = Math.max(blink_min, blink_max - heat * (blink_max - blink_min));
			intervalIdRef.current = setInterval(update_blink, blinkInterval);
		} else {
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current);
				intervalIdRef.current = null;
			}

			set_is_blink_on(false);
		}

		return () => {
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current);
				intervalIdRef.current = null;
			}
		};
	}, [heat]);

	const open_tracking_percent = 0.1;
	const open_tracking_ratio = side === 'left' ? (100 - open_tracking_percent) / 100 : (100 + open_tracking_percent) / 100;

	return (
		<Stack alignItems="center">
			<Tooltip title={'Current'} placement="top" arrow>
				<Typography variant="caption" color="grey">
					{get_price_str(current_price, true)}
				</Typography>
			</Tooltip>

			<Box display="flex" position="relative" width={width ?? '100%'} height="25px">
				<HorizontalBarGraph
					direction="left"
					barcolor={disabled ? disabled_color : 'error.main'}
					start_value={order_price}
					end_value={disabled ? order_price * open_tracking_ratio : loss_cut_price}
					current_value={current_price}
					line_mode={disabled}
					bgcolor={bgcolor}
				/>

				<HorizontalBarGraph
					direction="right"
					barcolor={disabled ? disabled_color : 'primary.main'}
					start_value={order_price}
					end_value={disabled ? order_price * open_tracking_ratio : target_price}
					current_value={current_price}
					line_mode={disabled}
					bgcolor={bgcolor}
				/>

				<Box
					id="name"
					height="100%"
					position="absolute"
					top={0}
					right={side === 'left' ? 10 : undefined}
					left={side === 'right' ? 10 : undefined}
					display="flex"
					alignItems="center"
				>
					<Typography variant="caption">{tag}</Typography>
				</Box>

				<Box
					id="middle-bar"
					zIndex={10}
					ref={intervalIdRef}
					width="1px"
					bgcolor={is_blink_on ? 'grey.400' : 'black'}
					height="100%"
					position="absolute"
					right="50%"
					top={0}
				/>

				<Box
					id="name"
					position="absolute"
					left="50%"
					display="flex"
					alignItems="center"
					justifyContent="center"
					color="black"
					height="100%"
					sx={{transform: 'translateX(-50%)'}}
				/>
			</Box>

			<Tooltip title={'Order'} placement="top" arrow>
				<Typography variant="caption" color="grey">
					{get_price_str(order_price, true)}
				</Typography>
			</Tooltip>
		</Stack>
	);
}
